'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/photos-documents.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/photos-documents#signatures)
 */

/**
 * FIELD SERVICE SIGNATURE TAB
 * 
 * Read-only view of customer signature captured by Vue mobile app.
 * Displays signature image, customer name, and capture metadata.
 * Also allows generating QR codes for remote signature requests.
 * 
 * Data Source: work_item_attachments table
 * - category: 'signature'
 * - metadata: { customer_name, gps_lat, gps_lng, captured_at, captured_by_name }
 * 
 * Date: November 10, 2025
 * Updated: November 30, 2025 - Added QR code generation for remote signatures
 */

import React, { useState, useEffect, useRef } from 'react';
import { PenTool, User, Clock, MapPin, CheckCircle, AlertCircle, QrCode, Copy, Check, RefreshCw, Loader2, Link, Smartphone, ChevronDown, UserCheck } from 'lucide-react';
import { authService } from '@/lib/authService';
import { fetchContactPeople } from '@/lib/contacts/fetchContactPeople';
import { useWorkItemDetailContext } from '@/contexts/WorkItemDetailContext';
import { QRCodeSVG } from 'qrcode.react';
import AppointmentReferenceBadge from '@/components/work-items/AppointmentReferenceBadge';
import AppointmentCompletionRequirementOverrideCard from '@/components/work-items/AppointmentCompletionRequirementOverrideCard';
import { PublicCommentsSummary, type PublicCommentItem } from '@/components/work-items/commentPresentation';
import UserDocsLink from '@/components/shared/UserDocsLink';
import type {
  AppointmentCompletionRequirementKey,
  AppointmentTypeAwareWorkItem,
  ConfigurableTypeOption,
} from '@/lib/work-items/appointmentTypeConfig';
import { notify } from '@/lib/notifications';

// ============================================================================
// INTERFACES
// ============================================================================

interface SignatureAttachment {
  id: string;
  file_name: string;
  file_path: string;
  category: 'signature';
  metadata: {
    customer_name?: string;
    signer_name?: string; // From QR code signatures
    designation?: string;
    gps_lat?: number;
    gps_lng?: number;
    gps_latitude?: number;
    gps_longitude?: number;
    captured_at?: string;
    signed_at?: string;
    captured_by_name?: string;
    signed_via?: 'direct' | 'qr_code';
    signature_data?: string; // Base64 fallback if file upload failed
    storage_url?: string;
    source_appointment_id?: string;
    source_appointment_number?: string;
    is_from_appointment?: boolean;
    closing_details_comments?: PublicCommentItem[];
  };
  uploaded_by_name?: string;
  created_at: string;
}

interface SignatureMediaRecord {
  id: string;
  data_url: string;
  work_item_id?: string | null;
  signer_name?: string | null;
  signer_role?: string | null;
  location?: {
    latitude: number;
    longitude: number;
  } | null;
  signed_at?: string | null;
  source?: 'qr_token' | 'direct_capture';
  source_appointment_id?: string | null;
  source_appointment_number?: string | null;
  is_from_appointment?: boolean;
  closing_details_comments?: PublicCommentItem[];
}

interface FieldServiceSignatureTabProps {
  workItemId: string;
  isActive: boolean;
  includeRelated?: boolean;
  contactId?: number | null;
  appointmentWorkItem?: AppointmentTypeAwareWorkItem | null;
  appointmentTypeOptions?: ConfigurableTypeOption[];
  onAppointmentRequirementOverrideChange?: (
    requirementKey: AppointmentCompletionRequirementKey,
    updates: { enabled?: boolean; reason?: string }
  ) => void;
}

interface ContactPerson {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  job_title?: string | null;
}

interface SignatureRequest {
  id: string;
  token: string;
  signature_url: string;
  qr_data: string;
  expires_at: string;
  status: 'pending' | 'used' | 'expired';
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FieldServiceSignatureTab({
  workItemId,
  isActive,
  includeRelated = true,
  contactId,
  appointmentWorkItem,
  appointmentTypeOptions,
  onAppointmentRequirementOverrideChange,
}: FieldServiceSignatureTabProps) {
  const detailContext = useWorkItemDetailContext();
  const [signatures, setSignatures] = useState<SignatureAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const latestLoadRequestIdRef = useRef(0);
  
  // QR Code state
  const [signatureRequest, setSignatureRequest] = useState<SignatureRequest | null>(null);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [copied, setCopied] = useState(false);

  // Contact people state
  const [contactPeople, setContactPeople] = useState<ContactPerson[]>([]);
  const [selectedSignerId, setSelectedSignerId] = useState<number | 'other' | 'none'>('other');
  const [signerDropdownOpen, setSignerDropdownOpen] = useState(false);
  const signerDropdownRef = useRef<HTMLDivElement>(null);
  const latestContactPeopleRequestIdRef = useRef(0);

  useEffect(() => {
    hasLoadedRef.current = false;
    setSignatures([]);
    setLoading(true);
    setSignatureRequest(null);
  }, [workItemId, includeRelated]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadSignature();
      hasLoadedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeRelated, isActive, workItemId]);

  // Fetch contact people when contactId is available (uses shared context)
  useEffect(() => {
    const requestId = latestContactPeopleRequestIdRef.current + 1;
    latestContactPeopleRequestIdRef.current = requestId;

    setContactPeople([]);
    setSelectedSignerId('other');
    setSignerDropdownOpen(false);

    if (!contactId) {
      return;
    }

    let cancelled = false;

    const fetchPeople = async () => {
      try {
        // Use shared context if available
        if (detailContext) {
          const people = await detailContext.loadContactPeople(contactId);
          if (cancelled || latestContactPeopleRequestIdRef.current !== requestId) {
            return;
          }

          const nextPeople = Array.isArray(people) ? (people as ContactPerson[]) : [];
          setContactPeople(nextPeople);
          setSelectedSignerId(nextPeople.length > 0 ? 'none' : 'other');
          return;
        }

        // Fallback: fetch directly
        const token = getAuthToken();
        if (!token) {
          return;
        }

        const people = await fetchContactPeople<ContactPerson>({
          contactId,
          token,
          activeOnly: true,
        });

        if (cancelled || latestContactPeopleRequestIdRef.current !== requestId) {
          return;
        }

        const nextPeople = Array.isArray(people) ? people : [];
        setContactPeople(nextPeople);
        setSelectedSignerId(nextPeople.length > 0 ? 'none' : 'other');
      } catch (err) {
        console.error('Error fetching contact people for signature tab:', err);

        if (cancelled || latestContactPeopleRequestIdRef.current !== requestId) {
          return;
        }

        setContactPeople([]);
        setSelectedSignerId('other');
      }
    };

    void fetchPeople();

    return () => {
      cancelled = true;
    };
  }, [contactId, detailContext]);

  // Close signer dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (signerDropdownRef.current && !signerDropdownRef.current.contains(e.target as Node)) {
        setSignerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function loadSignature() {
    const requestId = latestLoadRequestIdRef.current + 1;
    latestLoadRequestIdRef.current = requestId;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        work_item_id: workItemId,
        type: 'signatures',
      });
      if (includeRelated) params.set('include_related', 'true');

      const response = await fetch(`/api/v1/work-items/media/upload?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const rawSignatures = Array.isArray(data.signatures) ? data.signatures as SignatureMediaRecord[] : [];
          const filteredSignatures = appointmentWorkItem
            ? rawSignatures.filter((signature) => {
                const signatureWorkItemId = signature.work_item_id ? String(signature.work_item_id) : null;
                const sourceAppointmentId = signature.source_appointment_id ? String(signature.source_appointment_id) : null;
                const activeAppointmentId = String(workItemId);

                if (sourceAppointmentId) {
                  return sourceAppointmentId === activeAppointmentId;
                }

                return signatureWorkItemId === activeAppointmentId;
              })
            : rawSignatures;

          const signatureAttachments: SignatureAttachment[] = filteredSignatures.map((signature: SignatureMediaRecord) => ({
            id: String(signature.id),
            file_name: `signature-${signature.id}.png`,
            file_path: signature.data_url,
            category: 'signature',
            metadata: {
              customer_name: signature.signer_name || undefined,
              signer_name: signature.signer_name || undefined,
              designation: signature.signer_role || undefined,
              gps_latitude: signature.location?.latitude,
              gps_longitude: signature.location?.longitude,
              signed_at: signature.signed_at || undefined,
              captured_at: signature.signed_at || undefined,
              signed_via: signature.source === 'qr_token' ? 'qr_code' : 'direct',
              source_appointment_id: signature.source_appointment_id || undefined,
              source_appointment_number: signature.source_appointment_number || undefined,
              is_from_appointment: signature.is_from_appointment || undefined,
              closing_details_comments: Array.isArray(signature.closing_details_comments) ? signature.closing_details_comments : undefined,
            },
            uploaded_by_name: signature.signer_name || undefined,
            created_at: signature.signed_at || new Date().toISOString(),
          }));

          if (latestLoadRequestIdRef.current === requestId) {
            setSignatures(signatureAttachments);
          }
        } else if (latestLoadRequestIdRef.current === requestId) {
          setSignatures([]);
        }
      } else if (latestLoadRequestIdRef.current === requestId) {
        setSignatures([]);
      }
    } catch (error) {
      console.error('Error loading signature:', error);
      if (latestLoadRequestIdRef.current === requestId) {
        setSignatures([]);
      }
    } finally {
      if (latestLoadRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }

  // Generate QR code for remote signature
  const generateSignatureQR = async () => {
    setGeneratingQr(true);
    try {
      const token = getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`/api/v1/work-items/${workItemId}/signature-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ expires_in_minutes: 30 })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate QR code');

      setSignatureRequest({
        id: data.data.id,
        token: data.data.token,
        signature_url: data.data.signature_url,
        qr_data: data.data.qr_data,
        expires_at: data.data.expires_at,
        status: 'pending'
      });
    } catch (err: unknown) {
      console.error('QR generation error:', err);
      const message = err instanceof Error ? err.message : 'Failed to generate QR code';
      notify.error('Failed to generate QR code: ' + message);
    } finally {
      setGeneratingQr(false);
    }
  };

  // Copy signature URL to clipboard
  const copySignatureUrl = async () => {
    if (!signatureRequest?.signature_url) return;
    
    try {
      await navigator.clipboard.writeText(signatureRequest.signature_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = signatureRequest.signature_url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <PenTool className="w-12 h-12 text-gray-300 mx-auto mb-3 animate-pulse" />
          <p className="text-gray-500">Loading signature...</p>
        </div>
      </div>
    );
  }

  // Signer selector helper — reused in both no-signatures and additional-signature sections
  const handleSignerSelect = (id: number | 'other') => {
    setSelectedSignerId(id);
    setSignerDropdownOpen(false);
  };

  const getSelectedSignerName = (): string | null => {
    if (typeof selectedSignerId === 'number') {
      const person = contactPeople.find(p => p.id === selectedSignerId);
      return person ? `${person.first_name} ${person.last_name}`.trim() : null;
    }
    return null;
  };

  const renderSignerSelector = () => {
    if (contactPeople.length === 0) return null;
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Who should sign?</label>
        <div className="relative" ref={signerDropdownRef}>
          <button
            type="button"
            onClick={() => setSignerDropdownOpen(!signerDropdownOpen)}
            className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border text-sm transition-colors ${
              selectedSignerId === 'none'
                ? 'border-amber-300 bg-amber-50 text-amber-700'
                : 'border-gray-300 bg-white text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {selectedSignerId === 'none' ? (
                <><UserCheck className="w-4 h-4 text-amber-500 flex-shrink-0" /><span>Select signer...</span></>
              ) : selectedSignerId === 'other' ? (
                <><User className="w-4 h-4 text-gray-400 flex-shrink-0" /><span>Other person (enter details on signing page)</span></>
              ) : (() => {
                const p = contactPeople.find(cp => cp.id === selectedSignerId);
                return p ? (
                  <><UserCheck className="w-4 h-4 text-green-500 flex-shrink-0" /><span className="truncate">{p.first_name} {p.last_name}{p.job_title ? ` — ${p.job_title}` : ''}</span></>
                ) : null;
              })()}
            </div>
            <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${signerDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {signerDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 rounded-lg shadow-lg border border-gray-200 bg-white max-h-60 overflow-y-auto">
              {contactPeople.map(person => (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => handleSignerSelect(person.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 ${
                    selectedSignerId === person.id ? 'bg-green-50' : ''
                  }`}
                >
                  <UserCheck className={`w-4 h-4 flex-shrink-0 ${selectedSignerId === person.id ? 'text-green-500' : 'text-gray-400'}`} />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{person.first_name} {person.last_name}</div>
                    <div className="text-xs text-gray-500 flex gap-2">
                      {person.job_title && <span>{person.job_title}</span>}
                      {person.email && <span>{person.email}</span>}
                    </div>
                  </div>
                  {selectedSignerId === person.id && <CheckCircle className="w-4 h-4 text-green-500 ml-auto flex-shrink-0" />}
                </button>
              ))}
              <div className="border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => handleSignerSelect('other')}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 ${
                    selectedSignerId === 'other' ? 'bg-blue-50' : ''
                  }`}
                >
                  <User className={`w-4 h-4 flex-shrink-0 ${selectedSignerId === 'other' ? 'text-blue-500' : 'text-gray-400'}`} />
                  <div className="font-medium text-gray-700">Other person</div>
                </button>
              </div>
            </div>
          )}
        </div>
        {typeof selectedSignerId === 'number' && (() => {
          const person = contactPeople.find(p => p.id === selectedSignerId);
          return person ? (
            <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-green-50 rounded-lg text-sm text-green-700">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span>The signing page will be pre-filled with <strong>{person.first_name} {person.last_name}</strong>&apos;s details</span>
            </div>
          ) : null;
        })()}
      </div>
    );
  };

  if (signatures.length === 0) {
    return (
      <div className="space-y-6">
        <AppointmentCompletionRequirementOverrideCard
          workItem={appointmentWorkItem}
          appointmentTypeOptions={appointmentTypeOptions}
          requirementKey="signature"
          title="Customer signature"
          description="Use this only when the appointment type allows a signature override and the signer genuinely cannot provide a signature."
          placeholder="Explain why the customer signature can be bypassed for this appointment..."
          disabled={generatingQr}
          onChange={onAppointmentRequirementOverrideChange || (() => {})}
        />

        {/* No signatures message */}
        <div className="text-center py-8">
          <div className="max-w-md mx-auto">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg mb-2">No Signatures Captured</p>
            <p className="text-sm text-gray-400 mb-6">
              Customer signature will appear here once captured on mobile or via QR code
            </p>
          </div>
        </div>

        {/* QR Code Section */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <QrCode className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-semibold text-gray-900">Request Remote Signature</h3>
                <p className="text-sm text-gray-600">Generate a QR code or link for customer to sign remotely</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Signer selector (shown when contact people are available) */}
            {renderSignerSelector()}

            {signatureRequest && signatureRequest.status === 'pending' ? (
              <div className="space-y-6">
                {/* QR Code Display */}
                <div className="flex flex-col items-center py-4">
                  <div className="p-4 bg-white rounded-xl shadow-lg border">
                    <QRCodeSVG 
                      value={signatureRequest.qr_data}
                      size={200}
                      level="M"
                      includeMargin
                    />
                  </div>
                  <p className="mt-4 text-center text-gray-600">
                    Ask customer to scan this QR code
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Expires at {new Date(signatureRequest.expires_at).toLocaleTimeString()}
                  </p>
                </div>

                {/* Copy URL Section */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or copy this link to send via email/SMS:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={signatureRequest.signature_url}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 select-all"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      onClick={copySignatureUrl}
                      className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                        copied 
                          ? 'bg-green-600 text-white' 
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 rounded-xl p-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-bold text-blue-600">1</span>
                    </div>
                    <p className="text-sm text-blue-800">Customer scans QR with their phone camera or opens the link</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-bold text-blue-600">2</span>
                    </div>
                    <p className="text-sm text-blue-800">They enter their name and sign on their device</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-bold text-blue-600">3</span>
                    </div>
                    <p className="text-sm text-blue-800">Signature is automatically saved to this job</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setSignatureRequest(null)}
                    className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={generateSignatureQR}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Generate New Code
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Contactless Signature</h3>
                <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                  Generate a QR code or link for the customer to sign on their own device.
                  Perfect for remote approval or when the customer couldn't sign on-site.
                </p>
                <button
                  onClick={generateSignatureQR}
                  disabled={generatingQr}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 mx-auto disabled:opacity-50 transition-colors"
                >
                  {generatingQr ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <QrCode className="w-5 h-5" />
                      Generate QR Code
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-tour-id="work-item-signatures-tab">
      <AppointmentCompletionRequirementOverrideCard
        workItem={appointmentWorkItem}
        appointmentTypeOptions={appointmentTypeOptions}
        requirementKey="signature"
        title="Customer signature"
        description="Use this only when the appointment type allows a signature override and the signer genuinely cannot provide a signature."
        placeholder="Explain why the customer signature can be bypassed for this appointment..."
        disabled={generatingQr}
        onChange={onAppointmentRequirementOverrideChange || (() => {})}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-8 h-8 text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {signatures.length === 1 ? 'Customer Signature Captured' : `${signatures.length} Customer Signatures Captured`}
            </h3>
            <p className="text-sm text-gray-600">
              Job completion confirmed by customer signature{signatures.length > 1 ? 's' : ''}
            </p>
          </div>
          <UserDocsLink docsPath="/total-operations/workflows/service-operations/photos-documents#signatures" label="User guide: Photos & documents" />
        </div>
      </div>

      {/* Signature Cards */}
      {signatures.map((signature, index) => (
        <div key={signature.id} className="bg-white rounded-xl shadow-sm border-2 border-gray-200 overflow-hidden">
          {/* Customer Info Header */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-600">
                    {signatures.length > 1 ? `Signature #${index + 1} - Customer Name` : 'Customer Name'}
                  </span>
                  {signature.metadata.signed_via === 'qr_code' && (
                    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">QR Code</span>
                  )}
                  {signature.metadata.source_appointment_number && (
                    <AppointmentReferenceBadge
                      appointmentId={signature.metadata.source_appointment_id}
                      appointmentNumber={signature.metadata.source_appointment_number}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full font-medium hover:bg-indigo-200 transition-colors"
                      iconClassName="w-3 h-3"
                      title={`Open appointment ${signature.metadata.source_appointment_number}`}
                    >
                      <>
                        <span>Appt #{signature.metadata.source_appointment_number}</span>
                      </>
                    </AppointmentReferenceBadge>
                  )}
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {signature.metadata.signer_name || signature.metadata.customer_name || signature.uploaded_by_name || 'Not provided'}
                </p>
                {signature.metadata.designation && (
                  <p className="text-sm text-gray-600">{signature.metadata.designation}</p>
                )}
              </div>
              
              {(signature.metadata.gps_lat || signature.metadata.gps_latitude) && (signature.metadata.gps_lng || signature.metadata.gps_longitude) && (
                <div className="text-right">
                  <div className="flex items-center justify-end gap-2 mb-1">
                    <MapPin className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-600">GPS Verified</span>
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${signature.metadata.gps_lat || signature.metadata.gps_latitude},${signature.metadata.gps_lng || signature.metadata.gps_longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 underline"
                  >
                    View Location
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Signature Image */}
          <div className="p-8 bg-white">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
              <img
                src={signature.file_path.startsWith('http') ? signature.file_path : (signature.metadata.signature_data || signature.file_path)}
                alt={`Customer Signature ${signatures.length > 1 ? `#${index + 1}` : ''}`}
                className="w-full max-w-2xl mx-auto"
                style={{ maxHeight: '300px', objectFit: 'contain' }}
              />
            </div>
          </div>

          {Array.isArray(signature.metadata.closing_details_comments) && signature.metadata.closing_details_comments.length > 0 && (
            <div className="px-6 pb-6">
              <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                <PublicCommentsSummary
                  comments={signature.metadata.closing_details_comments}
                  title="Closing Details Signed Off"
                  maxHeightClassName="max-h-56"
                  showAcknowledgementNote={false}
                />
              </div>
            </div>
          )}

          {/* Metadata Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {/* Capture Time */}
              {(signature.metadata.captured_at || signature.metadata.signed_at) && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <div>
                    <span className="text-gray-600">Captured: </span>
                    <span className="font-medium text-gray-900">
                      {formatDateTime(signature.metadata.captured_at || signature.metadata.signed_at!)}
                    </span>
                  </div>
                </div>
              )}

              {/* Captured By */}
              {signature.metadata.captured_by_name && (
                <div className="flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-gray-500" />
                  <div>
                    <span className="text-gray-600">Captured by: </span>
                    <span className="font-medium text-gray-900">
                      {signature.metadata.captured_by_name}
                    </span>
                  </div>
                </div>
              )}

              {/* GPS Coordinates */}
              {signature.metadata.gps_lat && signature.metadata.gps_lng && (
                <div className="flex items-center gap-2 md:col-span-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <div>
                    <span className="text-gray-600">GPS: </span>
                    <span className="font-mono text-sm text-gray-900">
                      {signature.metadata.gps_lat.toFixed(6)}, {signature.metadata.gps_lng.toFixed(6)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Request Additional Signature Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => !signatureRequest && generateSignatureQR()}
          disabled={generatingQr}
          className={`w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${signatureRequest ? 'cursor-default' : ''}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <QrCode className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-medium text-gray-900">Request Additional Signature</h3>
              <p className="text-sm text-gray-500">Generate QR code or link for remote signing</p>
            </div>
          </div>
          {generatingQr && <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />}
        </button>
        
        {signatureRequest && signatureRequest.status === 'pending' && (
          <div className="border-t border-gray-200 p-6 space-y-4">
            {/* Signer selector for additional signatures */}
            {renderSignerSelector()}

            {/* QR Code Display */}
            <div className="flex flex-col items-center py-2">
              <div className="p-4 bg-white rounded-xl shadow-lg border">
                <QRCodeSVG 
                  value={signatureRequest.qr_data}
                  size={180}
                  level="M"
                  includeMargin
                />
              </div>
              <p className="mt-3 text-center text-gray-600 text-sm">
                Ask customer to scan this QR code
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Expires at {new Date(signatureRequest.expires_at).toLocaleTimeString()}
              </p>
            </div>

            {/* Copy URL Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or copy this link to send via email/SMS:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={signatureRequest.signature_url}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 select-all"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  onClick={copySignatureUrl}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                    copied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setSignatureRequest(null)}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
              <button
                onClick={generateSignatureQR}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                New Code
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Legal Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Signature Verification</p>
            <p className="text-blue-700">
              {signatures.length === 1 
                ? 'This signature was electronically captured on-site using GPS verification.'
                : 'These signatures were electronically captured on-site using GPS verification.'
              }
              {' '}The signature{signatures.length > 1 ? 's' : ''} serve{signatures.length === 1 ? 's' : ''} as confirmation that the customer{signatures.length > 1 ? 's' : ''} received and approved 
              the completed work.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
